// src/modules/communications/components/IncomingCallOverlay.js
import React, { useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useSoftphone } from '../context/SoftphoneContext';
import { FaPhone, FaPhoneSlash, FaUser } from 'react-icons/fa';

const pulseRing = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.15); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
`;

const ripple = keyframes`
  0% { transform: scale(1); opacity: 0.4; }
  100% { transform: scale(2.5); opacity: 0; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${props => props.theme.typography.fontFamily};
`;

const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 24px;
  padding: 40px 48px;
  text-align: center;
  box-shadow: 0 16px 64px rgba(0, 0, 0, 0.5);
  min-width: 340px;
  max-width: 420px;
`;

const AvatarContainer = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
`;

const Avatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary}33;
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  animation: ${pulseRing} 1.5s ease-in-out infinite;
  position: relative;
  z-index: 2;
`;

const RippleEffect = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid ${props => props.theme.colors.primary};
  animation: ${ripple} 2s ease-out infinite;
  z-index: 1;
`;

const Title = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 16px;
`;

const CallerName = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 8px;
`;

const CallerNumber = styled.div`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 32px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
`;

const ActionButton = styled.button`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s, box-shadow 0.15s;
  color: white;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
`;

const AnswerButton = styled(ActionButton)`
  background: #84c959;

  &:hover { background: #76b84e; }
`;

const DeclineButton = styled(ActionButton)`
  background: #FF5252;

  &:hover { background: #e04444; }
`;

const ButtonLabel = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-top: 8px;
  text-align: center;
`;

const formatPhoneDisplay = (number) => {
  if (!number) return 'Unknown';
  const digits = number.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return number;
};

const IncomingCallOverlay = () => {
  const { callState, callerInfo, answerCall, rejectCall } = useSoftphone();
  const navigate = useNavigate();

  const handleAnswer = useCallback(() => {
    answerCall();
    // Redirect to lead page if we know the lead
    if (callerInfo?.leadId) {
      navigate(`/leads/view/${callerInfo.leadId}`);
    }
  }, [answerCall, callerInfo, navigate]);

  if (callState !== 'incoming') return null;

  return (
    <Overlay>
      <Card>
        <AvatarContainer>
          <Avatar>
            <FaUser />
          </Avatar>
          <RippleEffect />
          <RippleEffect style={{ animationDelay: '0.5s' }} />
          <RippleEffect style={{ animationDelay: '1s' }} />
        </AvatarContainer>

        <Title>Incoming Call</Title>

        <CallerName>
          {callerInfo?.name || 'Unknown Caller'}
        </CallerName>

        <CallerNumber>
          {formatPhoneDisplay(callerInfo?.number)}
        </CallerNumber>

        <ButtonRow>
          <div>
            <DeclineButton onClick={rejectCall}>
              <FaPhoneSlash />
            </DeclineButton>
            <ButtonLabel>Decline</ButtonLabel>
          </div>
          <div>
            <AnswerButton onClick={handleAnswer}>
              <FaPhone />
            </AnswerButton>
            <ButtonLabel>Answer</ButtonLabel>
          </div>
        </ButtonRow>
      </Card>
    </Overlay>
  );
};

export default IncomingCallOverlay;
